import { useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import { FeedbackInfo } from './useGatherFeedback'

import { FeedbackType } from '@/components/layout-components/Feedback/FeedbackSection'

interface UseFeedbackProps {
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export type FeedbackPayload = {
  type: FeedbackType
  message: string
  attachments?: Array<{ url: string; name: string }>
  metadata?: FeedbackInfo
  rating?: number
  userId: string
}

export interface FormattedFeedback {
  type: string
  message: string
  rating?: number | string
  user: {
    name?: string
    email?: string
    id?: string
    roles?: string[]
  }
  organization: {
    name?: string
    id?: string
    subscription?: string
  }
  project: {
    name?: string
    id?: string
    status?: string
  }
  navigation: {
    path?: string
    pageName?: string
    pageId?: string
    pagePath?: string
  }
  location: {
    country?: string
    language?: string
    locale?: string
    systemTimezone?: string
    userTimezone?: string
  }
  browser: {
    userAgent?: string
    viewport?: {
      width?: string
      height?: string
    }
  }
  attachments: Array<{ url: string; name: string }>
}

export const formatMessageBlock = (payload: FeedbackPayload) => {
  const sections = [
    `Type: ${payload.type?.toUpperCase() ?? 'No type provided'}`,
    `Message: ${payload.message ?? 'No message provided'}`,
    payload.rating && `Rating: ${payload.rating}/5`,
    '',
    '--- User Details ---',
    payload.metadata?.user &&
      [
        `Name: ${payload.metadata.user.firstName} ${payload.metadata.user.lastName}`,
        `Email: ${payload.metadata.user.email}`,
        `ID: ${payload.metadata.user.id}`,
        `Roles: ${payload.metadata.user.roles?.join(', ')}`,
      ]
        .filter(Boolean)
        .join('\n'),
    '',
    '--- Builder Details ---',
    payload.metadata?.builder &&
      [
        `Company: ${payload.metadata.builder.company}`,
        `Subscription: ${payload.metadata.builder.subscription}`,
        `ID: ${payload.metadata.builder.id}`,
      ]
        .filter(Boolean)
        .join('\n'),
    '',
    '--- Project Details ---',
    payload.metadata?.project &&
      [
        `Name: ${payload.metadata.project.name}`,
        `ID: ${payload.metadata.project.id}`,
        `Status: ${payload.metadata.project.status}`,
      ]
        .filter(Boolean)
        .join('\n'),
    '',
    '--- Navigation Details ---',
    payload.metadata?.navigation &&
      [
        `Path: ${payload.metadata.navigation.currentPath}`,
        payload.metadata.navigation.currentNavItem &&
          `Page: ${payload.metadata.navigation.currentNavItem.name}`,
      ]
        .filter(Boolean)
        .join('\n'),
    payload.metadata?.location &&
      [
        `--- Location & Locale Details ---`,
        `Country: ${payload.metadata.location.country}`,
        `Language: ${payload.metadata.location.language}`,
        `Timezone: ${payload.metadata.location.timezone}`,
        `Locale: ${payload.metadata.location.locale}`,
      ]
        .filter(Boolean)
        .join('\n'),
    payload.metadata?.browser &&
      [
        `--- Browser Details ---`,
        `Viewport: ${payload.metadata.browser.viewport?.width}x${payload.metadata.browser.viewport?.height}`,
        `User Agent: ${payload.metadata.browser.userAgent}`,
      ]
        .filter(Boolean)
        .join('\n'),
    payload.attachments?.length &&
      [
        '',
        '--- Attachments ---',
        payload.attachments.map((a) => `${a.name}: ${a.url}`).join('\n'),
      ]
        .filter(Boolean)
        .join('\n'),
  ]
    .filter(Boolean)
    .join('\n')

  return sections
}

const postFeedback = async (endpoint: string, payload: FeedbackPayload) => {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }))
    throw new Error(
      `Failed to submit feedback to ${endpoint}: ${error.message}`
    )
  }

  return response.json()
}

export const useSubmitFeedback = ({
  onSuccess,
  onError,
}: UseFeedbackProps = {}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submitFeedback = async (payload: FeedbackPayload) => {
    setIsSubmitting(true)

    // Send all feedback types to Intercom for CX team to review
    try {
      await postFeedback('/api/postFeedbackToIntercom', payload)
    } catch (err) {
      console.error('Failed to create Intercom conversation:', err)
      Sentry.captureException(err, {
        extra: {
          message: 'Failed to create Intercom conversation',
          context: 'useSubmitFeedback',
          payload,
        },
      })
    }

    // Only send IDEA and OTHER feedback types to Linear
    if (payload.type !== 'issue') {
      try {
        await postFeedback('/api/postFeedbackToLinear', payload)
      } catch (err) {
        console.error('Failed to create Linear issue:', err)
        Sentry.captureException(err, {
          extra: {
            message: 'Failed to create Linear issue',
            context: 'useSubmitFeedback',
            payload,
          },
        })
      }
    }

    try {
      await postFeedback('/api/postFeedbackToSlack', payload)
      onSuccess?.()
    } catch (err) {
      console.error('Failed to post feedback to Slack:', err)
      Sentry.captureException(err, {
        extra: {
          message: 'Failed to post feedback to Slack',
          context: 'useSubmitFeedback',
          payload,
        },
      })
      onError?.(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    submitFeedback,
    isSubmitting,
  }
}
